import { z } from './zodMini';

export type AuthErrorCode =
  | 'invalid-credentials'
  | 'lockout-imminent'
  | 'locked'
  | 'validation-failed'
  | 'network-error'
  | 'server-error'
  | 'unknown';

/**
 * The fallback code for a non-ok response nothing else classified. A 5xx body's
 * `error` is a status phrase ("Internal Server Error"), not something the server
 * explained about *this* request, so it gets its own code — renderers read the
 * code to choose their own copy instead of quoting Rails at the user. The
 * message still passes through for the reporter.
 */
function unclassifiedErrorCode(status: number): AuthErrorCode {
  return status >= 500 ? 'server-error' : 'unknown';
}

export interface AuthError {
  code: AuthErrorCode;
  /**
   * Raw message from Rails, when present. Never branch on this — branch on
   * `code` — and never render it without checking the code first: on a
   * `server-error` it is a status phrase, not user-facing copy.
   */
  message: string;
  /** Per-field validation messages, when Rails returns them (registration only). */
  fieldErrors?: Record<string, string[]>;
}

export type AuthResult<T> = { success: true; data: T } | { success: false; error: AuthError };

interface LoginErrorPattern {
  code: AuthErrorCode;
  matches: (message: string) => boolean;
}

/**
 * Prose fallback for classifying a /login failure, used only when the wire
 * `code` is absent or unrecognized. A lookup table rather than inline
 * conditionals so a new lockout-copy variant is a new row here, not a new
 * branch at every call site. The substrings are Devise's lockout copy, so a
 * Rails-side wording change breaks classification silently.
 */
const LOGIN_ERROR_PATTERNS: LoginErrorPattern[] = [
  {
    code: 'lockout-imminent',
    matches: (message) =>
      message.includes('You have one more attempt before your account is locked.'),
  },
  {
    code: 'locked',
    matches: (message) => message.includes('Your account is locked.'),
  },
];

/** The stable failure codes Rails emits on the /login error body. */
const loginWireCodeSchema = z.enum(['locked', 'last_attempt', 'invalid']);
type LoginWireCode = z.infer<typeof loginWireCodeSchema>;

const WIRE_CODE_TO_AUTH_ERROR_CODE = {
  locked: 'locked',
  last_attempt: 'lockout-imminent',
  invalid: 'invalid-credentials',
} satisfies Record<LoginWireCode, AuthErrorCode>;

function readLoginWireCode(value: unknown): LoginWireCode | undefined {
  const parsed = loginWireCodeSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Classifies a /login failure, preferring the wire `code` and falling back to
 * matching the error prose only when the code is absent or unrecognized.
 */
function classifyLoginError(
  status: number,
  message: string,
  wireCode?: LoginWireCode | null,
): AuthError {
  if (wireCode) {
    return { code: WIRE_CODE_TO_AUTH_ERROR_CODE[wireCode], message };
  }
  const pattern = LOGIN_ERROR_PATTERNS.find((candidate) => candidate.matches(message));
  if (pattern) {
    return { code: pattern.code, message };
  }
  // Neither a 5xx nor an empty message is a credential outcome. A real Devise
  // 401 always carries prose (and, since the wire-code rollout, a code), while
  // Rails' `{"error":"Internal Server Error"}` carries prose that would
  // otherwise read as a rejected password. Both classify as non-credential
  // failures so reporter gates like `isExpectedLoginFailure` stay open.
  if (status >= 500 || message === '') {
    return { code: unclassifiedErrorCode(status), message };
  }
  return { code: 'invalid-credentials', message };
}

const LOCALHOST_HOSTNAME = 'localhost';

function csrfCookieName(): string {
  return window.location.hostname === LOCALHOST_HOSTNAME ? 'XSRF-TOKEN' : 'XSRF-G-TOKEN';
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const entry = document.cookie.split('; ').find((candidate) => candidate.startsWith(prefix));
  if (!entry) return undefined;
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    // A malformed cookie value (e.g. a lone '%') must not surface as a
    // misleading network-error from the caller's unrelated fetch try/catch.
    return undefined;
  }
}

function postJson(
  authBaseUrl: string,
  path: string,
  body: unknown,
  fetchFn: typeof fetch,
): Promise<Response> {
  return fetchFn(`${authBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': readCookie(csrfCookieName()) ?? '',
    },
    body: JSON.stringify(body),
  });
}

function getJson(authBaseUrl: string, path: string, fetchFn: typeof fetch): Promise<Response> {
  return fetchFn(`${authBaseUrl}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
}

/**
 * Reads and parses a response body against `schema`, never throwing —
 * `AuthResult` is the module's whole contract, so an unparsable or
 * shape-mismatched body must degrade to `undefined`, not reject the promise.
 */
async function parseBody<T>(
  response: Response,
  schema: { safeParse(data: unknown): { success: true; data: T } | { success: false } },
): Promise<T | undefined> {
  const raw: unknown = await response.json().catch(() => undefined);
  const result = schema.safeParse(raw);
  return result.success ? result.data : undefined;
}

// ---------------------------------------------------------------------------
// logIn — POST /login
// ---------------------------------------------------------------------------

const logInResponseSchema = z.object({
  // nullish, not optional: Rails serializes an absent field as JSON `null`
  // (e.g. `{ error: "...", redirect_to: null }`), which optional rejects.
  redirect_to: z.nullish(z.string()),
  error: z.nullish(z.string()),
  // Any JSON value. An unknown future code is dropped in readLoginWireCode
  // so it cannot fail the rest of this body.
  code: z.nullish(z.unknown()),
  // Accepted for forward-compat but not branched on — the fetch Response
  // already carries the HTTP status.
  status: z.nullish(z.number()),
});

export interface LogInResponse {
  redirectTo?: string;
}

export type LogInCredentials = { email: string; password: string };

export async function logIn(
  authBaseUrl: string,
  credentials: LogInCredentials,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<LogInResponse>> {
  const body = { human: { email: credentials.email, password: credentials.password } };

  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/login', body, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, logInResponseSchema);
  if (response.ok) {
    return { success: true, data: { redirectTo: data?.redirect_to ?? undefined } };
  }
  return {
    success: false,
    error: classifyLoginError(response.status, data?.error ?? '', readLoginWireCode(data?.code)),
  };
}

// ---------------------------------------------------------------------------
// register — POST /register
// ---------------------------------------------------------------------------

const registerResponseSchema = z.object({
  redirect_to: z.nullish(z.string()),
  error: z.nullish(z.string()),
  // Two real shapes: Devise's default validation failure renders a field→messages
  // object; the handle_invalid_phone_number rescue (registrations_controller.rb)
  // renders a flat message array instead.
  errors: z.nullish(z.union([z.record(z.string(), z.array(z.string())), z.array(z.string())])),
});

export interface RegisterResponse {
  redirectTo?: string;
}

export type RegisterParams = {
  email: string;
  fullName: string;
  primaryPhone: string;
  redirectUrl?: string;
} & (
  | { password: string }
  // Passwordless: Rails auto-generates a server-side password and (behind the
  // register_send_password_setup flag) mails a setup link, so no credential
  // rides on the request. See Humans::RegistrationsController#create.
  | { hasGeneratedPassword: true }
);

export async function register(
  authBaseUrl: string,
  params: RegisterParams,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<RegisterResponse>> {
  const body = {
    email: params.email,
    password: 'password' in params ? params.password : undefined,
    has_generated_password: 'hasGeneratedPassword' in params ? true : undefined,
    full_name: params.fullName,
    primary_phone: params.primaryPhone,
    redirect_url: params.redirectUrl,
  };

  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/register', body, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, registerResponseSchema);
  if (response.ok) {
    return { success: true, data: { redirectTo: data?.redirect_to ?? undefined } };
  }
  if (data?.errors) {
    const fieldErrors = Array.isArray(data.errors) ? { base: data.errors } : data.errors;
    const message = Array.isArray(data.errors) ? (data.errors[0] ?? '') : (data.error ?? '');
    return { success: false, error: { code: 'validation-failed', message, fieldErrors } };
  }
  return {
    success: false,
    error: { code: unclassifiedErrorCode(response.status), message: data?.error ?? '' },
  };
}

// ---------------------------------------------------------------------------
// loginWithPasswordlessToken — POST /register with a one-time passwordless token
// ---------------------------------------------------------------------------

const passwordlessTokenLoginResponseSchema = z.object({
  redirect_to: z.nullish(z.string()),
  error: z.nullish(z.string()),
});

export interface PasswordlessTokenLoginParams {
  /** The one-time `access_token` from a login-lite / mobile-handoff link. */
  accessToken: string;
  redirectUrl?: string;
}

export interface PasswordlessTokenLoginResponse {
  redirectTo?: string;
}

/**
 * Logs a returning human in by redeeming a one-time passwordless token. Despite
 * the `login` name it POSTs `/register` with `passwordless_token` — Rails routes
 * that param to the PasswordlessAuthenticatable strategy
 * (`Humans::RegistrationsController#create`), the same endpoint and body shaping
 * the legacy session saga used, so the wire contract is unchanged. The response
 * carries the server's post-login destination in `redirect_to`; the login-lite
 * pages compute their own redirect from the URL, so this is surfaced for parity
 * rather than consumed.
 */
export async function loginWithPasswordlessToken(
  authBaseUrl: string,
  params: PasswordlessTokenLoginParams,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<PasswordlessTokenLoginResponse>> {
  const body = {
    passwordless_token: params.accessToken,
    redirect_url: params.redirectUrl,
  };

  // Failure taxonomy is deliberately coarse: a thrown fetch is a `network-error`,
  // and a non-ok response is typed from its HTTP status. Rails renders every
  // passwordless-token failure as a bare 401 with an empty JSON body — unlike the
  // native-strategy login failures, which carry a stable `error` code — so a
  // rejected token (expired, already used, unknown, or not matching the current
  // session) is indistinguishable on the wire. A 401 therefore maps to
  // `invalid-credentials` (the token was rejected), a 5xx to `server-error`, and
  // any other non-ok status to `unknown`. `message` passes through if the server
  // ever supplies one; finer codes need the server to emit them on this branch.
  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/register', body, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, passwordlessTokenLoginResponseSchema);
  if (response.ok) {
    return { success: true, data: { redirectTo: data?.redirect_to ?? undefined } };
  }

  const code: AuthErrorCode =
    response.status === 401 ? 'invalid-credentials' : unclassifiedErrorCode(response.status);
  return { success: false, error: { code, message: data?.error ?? '' } };
}

// ---------------------------------------------------------------------------
// requestHumanStatus — GET /api/v1/humans/status
// ---------------------------------------------------------------------------

// Rails signals "no account for this email" as a 404 with an empty JSON
// object body (`api/v1/humans#status` renders `{}` + :not_found; an `error`
// key marks a real failure). A JSON `null` body is honored as the same
// signal. Neither is part of this schema, so a malformed existing-human body
// (a parse failure) isn't silently conflated with "no account".
const humanStatusNotFoundSchema = z.object({ error: z.optional(z.never()) });
const humanStatusResponseSchema = z.object({
  has_generated_password: z.nullish(z.boolean()),
});

export interface HumanStatusResponse {
  isNewHuman: boolean;
  hasGeneratedPassword: boolean;
}

export async function requestHumanStatus(
  authBaseUrl: string,
  email: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<HumanStatusResponse>> {
  let response: Response;
  try {
    response = await getJson(
      authBaseUrl,
      `/api/v1/humans/status?email=${encodeURIComponent(email)}`,
      fetchFn,
    );
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  if (response.status === 404) {
    const raw: unknown = await response.json().catch(() => undefined);
    // A non-JSON 404 (e.g. a route miss on a misconfigured base URL) stays a
    // failure rather than reading as "every visitor is new".
    if (raw === null || humanStatusNotFoundSchema.safeParse(raw).success) {
      return { success: true, data: { isNewHuman: true, hasGeneratedPassword: false } };
    }
    return { success: false, error: { code: 'unknown', message: '' } };
  }

  if (!response.ok) {
    return { success: false, error: { code: unclassifiedErrorCode(response.status), message: '' } };
  }

  const raw: unknown = await response.json().catch(() => undefined);
  if (raw === null) {
    return { success: true, data: { isNewHuman: true, hasGeneratedPassword: false } };
  }

  const parsed = humanStatusResponseSchema.safeParse(raw);
  if (!parsed.success) {
    return { success: false, error: { code: 'unknown', message: '' } };
  }
  return {
    success: true,
    data: { isNewHuman: false, hasGeneratedPassword: Boolean(parsed.data.has_generated_password) },
  };
}

// ---------------------------------------------------------------------------
// requestResetPassword — POST /api/v1/humans/reset-password
// ---------------------------------------------------------------------------

const resetPasswordResponseSchema = z.object({ error: z.nullish(z.string()) });

export async function requestResetPassword(
  authBaseUrl: string,
  email: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<undefined>> {
  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/api/v1/humans/reset-password', { email }, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, resetPasswordResponseSchema);
  if (response.ok) {
    return { success: true, data: undefined };
  }
  return {
    success: false,
    error: { code: unclassifiedErrorCode(response.status), message: data?.error ?? '' },
  };
}

// ---------------------------------------------------------------------------
// requestPasswordReset — GET /humans/status, then POST reset for existing accounts
// ---------------------------------------------------------------------------

/**
 * The human-status-guarded reset flow the UI drives: look the email up first,
 * and only ask Rails to send a reset/setup email when an account exists. A new
 * email resolves successfully with `isNewHuman: true` and no POST, so the
 * caller routes to registration rather than claiming a reset email was sent.
 * Mirrors the legacy saga's `requestPasswordReset` + `maybeResetPassword`
 * branching (`shouldResetPassword = !isNewHuman`).
 */
export async function requestPasswordReset(
  authBaseUrl: string,
  email: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<HumanStatusResponse>> {
  const statusResult = await requestHumanStatus(authBaseUrl, email, fetchFn);
  if (!statusResult.success || statusResult.data.isNewHuman) {
    return statusResult;
  }

  const resetResult = await requestResetPassword(authBaseUrl, email, fetchFn);
  if (!resetResult.success) {
    return resetResult;
  }
  return statusResult;
}
